/**
 * Reunião SACAP e circular.
 *
 * A reunião é o contêiner do tratamento de não-conformidade: é nela que o
 * Ishikawa acontece, que as causas-raiz saem e que o plano de ação nasce.
 *
 * Referência: docs/legacy-spec/sgq.md seções B e D.
 */
import { AttendanceStatus, CircularStatus } from '@qualicloud/contracts';
import { StateMachine } from './state-machine.js';
/**
 * Reexportados, NÃO redefinidos — o valor persistido é contrato. Ver a mesma
 * decisão em `capa.ts`.
 */
export { AttendanceStatus, CircularStatus };
// ─────────────────────────────────────────────────────────────────────────────
// Reunião
// ─────────────────────────────────────────────────────────────────────────────
/**
 * No legado o estado da reunião **não tinha coluna**: era derivado do tempo e de
 * `dt_cancela`/`dt_encerra` por um `CASE` em SQL (`model/reuniao/reuniao.php:424-430`).
 * Consequência prática: uma reunião "acontecia" sozinha quando o relógio passava
 * do horário, mesmo que ninguém tivesse comparecido, e não havia como distinguir
 * "ocorreu" de "o horário passou".
 */
export const MeetingStatus = {
    /** Agendada, aguardando a data. Legado: 'I'. */
    SCHEDULED: 'SCHEDULED',
    /** Em andamento. Legado: 'S' (derivado do relógio). */
    IN_PROGRESS: 'IN_PROGRESS',
    /** Horário passou sem encerramento formal. Legado: 'A' (derivado). */
    AWAITING_CLOSURE: 'AWAITING_CLOSURE',
    /** Encerrada com ata emitida. Legado: 'F'. */
    CLOSED: 'CLOSED',
    /** Aguardando avaliação de eficácia da SACAP. Legado: 'W'. */
    AWAITING_EFFECTIVENESS: 'AWAITING_EFFECTIVENESS',
    /** Cancelada. Legado: 'C'. */
    CANCELLED: 'CANCELLED',
};
export const MeetingEvent = {
    START: 'START',
    MISS_SCHEDULE: 'MISS_SCHEDULE',
    CLOSE: 'CLOSE',
    REQUIRE_EFFECTIVENESS: 'REQUIRE_EFFECTIVENESS',
    RECORD_EFFECTIVENESS: 'RECORD_EFFECTIVENESS',
    CANCEL: 'CANCEL',
};
export const meetingMachine = new StateMachine('Meeting', [
    {
        from: MeetingStatus.SCHEDULED,
        to: MeetingStatus.IN_PROGRESS,
        event: MeetingEvent.START,
        description: 'Alguém ABRE a reunião. Ato explícito, não decurso de horário — o legado ' +
            'mudava o estado sozinho quando o relógio passava.',
    },
    {
        from: MeetingStatus.SCHEDULED,
        to: MeetingStatus.AWAITING_CLOSURE,
        event: MeetingEvent.MISS_SCHEDULE,
        description: 'O horário passou e ninguém abriu. Job diário materializa. Distinguir isto ' +
            'de "ocorreu" importa: uma reunião que não aconteceu não gera ata.',
    },
    {
        from: MeetingStatus.IN_PROGRESS,
        to: MeetingStatus.AWAITING_CLOSURE,
        event: MeetingEvent.MISS_SCHEDULE,
        description: 'Aberta e não encerrada no dia.',
    },
    {
        from: MeetingStatus.IN_PROGRESS,
        to: MeetingStatus.CLOSED,
        event: MeetingEvent.CLOSE,
        requiresSignatureAct: 'MEETING_CLOSE',
        description: 'Encerramento com emissão de ata assinada.',
    },
    {
        from: MeetingStatus.AWAITING_CLOSURE,
        to: MeetingStatus.CLOSED,
        event: MeetingEvent.CLOSE,
        requiresSignatureAct: 'MEETING_CLOSE',
        description: 'Encerramento tardio, com ata assinada.',
    },
    {
        from: MeetingStatus.CLOSED,
        to: MeetingStatus.AWAITING_EFFECTIVENESS,
        event: MeetingEvent.REQUIRE_EFFECTIVENESS,
        description: 'Reunião do tipo SACAP encerrada passa a aguardar a avaliação de eficácia. ' +
            'No legado a condição era `tipo LIKE "%SACAP%"` — casamento por NOME, que ' +
            'quebrava se alguém renomeasse o tipo. Aqui é uma flag do tipo de reunião.',
    },
    {
        from: MeetingStatus.AWAITING_EFFECTIVENESS,
        to: MeetingStatus.CLOSED,
        event: MeetingEvent.RECORD_EFFECTIVENESS,
        description: 'Eficácia da SACAP registrada; a reunião volta a encerrada.',
    },
    {
        from: MeetingStatus.SCHEDULED,
        to: MeetingStatus.CANCELLED,
        event: MeetingEvent.CANCEL,
        description: 'Cancelamento antes de começar.',
    },
    {
        from: MeetingStatus.IN_PROGRESS,
        to: MeetingStatus.CANCELLED,
        event: MeetingEvent.CANCEL,
        description: 'Cancelamento durante.',
    },
    {
        from: MeetingStatus.AWAITING_CLOSURE,
        to: MeetingStatus.CANCELLED,
        event: MeetingEvent.CANCEL,
        description: 'Reunião que não aconteceu, cancelada em vez de encerrada.',
    },
]);
export class MeetingHasNoParticipantsError extends Error {
    constructor() {
        super('A reunião precisa de ao menos um participante. ' +
            'Uma SACAP sem participantes não produz ata nem evidência de análise crítica.');
        this.name = 'MeetingHasNoParticipantsError';
    }
}
export class MeetingLinkRequiredError extends Error {
    /**
     * A mensagem nomeia o TIPO, e não "SACAP".
     *
     * Dizer "uma SACAP precisa..." era verdade enquanto SACAP era o único tipo
     * exigente e o nome estava cravado no código. Com o catálogo administrável, a
     * organização pode ter criado "Análise de causa da linha 2" com a mesma
     * exigência — e uma mensagem que fala de SACAP para quem escolheu outro tipo
     * manda a pessoa procurar um campo que a tela dela não tem.
     */
    constructor(typeLabel) {
        super(`O tipo "${typeLabel}" exige vínculo com ao menos uma não-conformidade. ` +
            'É uma reunião para tratar NC — sem vínculo, não há o que analisar.');
        this.name = 'MeetingLinkRequiredError';
    }
}
/**
 * Invariantes do encerramento.
 *
 * DIVERGÊNCIA D-MEET-01: no legado, `updateReuniao` sem `nc[]` no POST
 * **apagava todos os vínculos de NC** da reunião, silenciosamente — a mesma
 * classe do bug de distribuição no GED. E `updateReuniao` sequestrava a autoria,
 * gravando `id_usuario` = usuário logado, de modo que quem editasse a pauta
 * virava o autor da reunião. Ambos corrigidos.
 */
export function assertMeetingCanBeClosed(params) {
    if (params.participantCount === 0)
        throw new MeetingHasNoParticipantsError();
    if (params.requiresNonconformity && params.linkedNonconformityCount === 0) {
        throw new MeetingLinkRequiredError(params.typeLabel);
    }
}
/**
 * A mesma coerência, cobrada no AGENDAMENTO.
 *
 * Existe separada do encerramento porque o momento é outro e a consequência
 * também: aqui a reunião ainda não existe, e barrar agora evita que ela nasça
 * impossível de encerrar. Era o que o `superRefine` do contrato fazia enquanto a
 * regra cabia em `kind === 'SACAP'`; saiu de lá porque o contrato não conhece as
 * flags do tipo — só o servidor, depois de carregá-lo, conhece.
 */
export function assertMeetingTypeCoherence(params) {
    if (params.requiresNonconformity && params.linkedNonconformityCount === 0) {
        throw new MeetingLinkRequiredError(params.typeLabel);
    }
}
export class NoRootCauseError extends Error {
    constructor() {
        super('Registre ao menos uma causa-raiz antes de definir o plano de ação. ' +
            'ISO 13485 §8.5.2 exige determinar a causa da não-conformidade antes de agir ' +
            'sobre ela — sem isso a "ação corretiva" trata o sintoma.');
        this.name = 'NoRootCauseError';
    }
}
/**
 * O plano de ação só pode nascer de uma causa-raiz.
 *
 * Este é o elo que o legado tinha na estrutura e não impunha: era possível criar
 * tarefa solta, sem causa, e a maioria das tarefas reais nasceu assim — pela
 * tela `reuniao/addSS`, que nem sequer persistia os campos 5W2H (BUG-TAR-01).
 */
export function assertHasRootCause(rootCauseCount) {
    if (rootCauseCount === 0)
        throw new NoRootCauseError();
}
export class AlreadyAcknowledgedError extends Error {
    constructor() {
        super('Você já confirmou a leitura desta circular. ' +
            'A primeira confirmação é o registro válido e não pode ser substituída.');
        this.name = 'AlreadyAcknowledgedError';
    }
}
export function assertNotYetAcknowledged(existing) {
    if (existing)
        throw new AlreadyAcknowledgedError();
}
/**
 * Cobertura de leitura — o indicador que a circular existe para produzir.
 * Sem ele, "a equipe foi comunicada" é afirmação sem evidência.
 */
export function acknowledgementCoverage(params) {
    const pending = Math.max(0, params.recipientCount - params.acknowledgedCount);
    const percentage = params.recipientCount === 0
        ? 100
        : Math.round((params.acknowledgedCount / params.recipientCount) * 1000) / 10;
    return { pending, percentage };
}
//# sourceMappingURL=meeting.js.map